'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

type OrderStatus = 'open' | 'matched' | 'in_transit' | 'delivered' | 'completed' | 'cancelled' | 'disputed';

interface BidOnOrder {
  id: string;
  tripId: string | null;
  bidPrice: number | null;
  message: string;
  status: 'pending' | 'accepted' | 'rejected';
  createdAt: string;
  travelerName: string;
  travelerUsername: string;
  travelerRating: number | null;
  travelerId: string;
}

interface OrderRecord {
  id: string;
  pickupCity: string;
  destinationCity: string;
  parcelCategory: string;
  weightKg: number;
  offeredPriceKes: number;
  deliveryDeadline: string | null;
  status: OrderStatus;
  createdAt: string;
  bids: BidOnOrder[];
  // Delivery info (if matched/in_transit/delivered)
  deliveryStatus: string | null;
  deliveryId: string | null;
  travelerName: string | null;
  travelerUsername: string | null;
}

interface SummaryStats {
  total: number;
  open: number;
  matched: number;
  inTransit: number;
  delivered: number;
  completed: number;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const orderStatusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' | 'muted' | 'info'; icon: string; bg: string }> = {
  open:       { label: 'Open',        variant: 'warning', icon: 'ClockIcon',           bg: 'bg-amber-50 border-amber-200' },
  matched:    { label: 'Matched',     variant: 'info',    icon: 'LinkIcon',            bg: 'bg-blue-50 border-blue-200' },
  in_transit: { label: 'In Transit',  variant: 'warning', icon: 'TruckIcon',           bg: 'bg-orange-50 border-orange-200' },
  delivered:  { label: 'Delivered',   variant: 'success', icon: 'CheckCircleIcon',     bg: 'bg-emerald-50 border-emerald-200' },
  completed:  { label: 'Completed',   variant: 'success', icon: 'CheckBadgeIcon',      bg: 'bg-green-50 border-green-200' },
  cancelled:  { label: 'Cancelled',   variant: 'muted',   icon: 'XCircleIcon',         bg: 'bg-muted/30 border-border' },
  disputed:   { label: 'Disputed',    variant: 'danger',  icon: 'ExclamationCircleIcon', bg: 'bg-red-50 border-red-200' },
};

const bidStatusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' | 'muted'; color: string }> = {
  pending:  { label: 'Pending',  variant: 'warning', color: 'text-amber-700 bg-amber-100' },
  accepted: { label: 'Accepted', variant: 'success', color: 'text-emerald-700 bg-emerald-100' },
  rejected: { label: 'Rejected', variant: 'danger',  color: 'text-red-600 bg-red-100' },
};

const filterOptions: { label: string; value: 'all' | OrderStatus }[] = [
  { label: 'All Orders',  value: 'all' },
  { label: 'Open',        value: 'open' },
  { label: 'Matched',     value: 'matched' },
  { label: 'In Transit',  value: 'in_transit' },
  { label: 'Delivered',   value: 'delivered' },
  { label: 'Completed',   value: 'completed' },
];

// ─── Helper: format date + time ───────────────────────────────────────────────
function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString('en-KE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function formatDateOnly(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function MyOrdersContent() {
  const { user } = useAuth();
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | OrderStatus>('all');
  const [search, setSearch] = useState('');
  const [expandedBids, setExpandedBids] = useState<Set<string>>(new Set());
  const [liveUpdate, setLiveUpdate] = useState(false);
  const [acceptingBid, setAcceptingBid] = useState<string | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const liveFlashRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchOrders = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);

    const supabase = createClient();

    const { data, error: fetchError } = await supabase
      .from('orders')
      .select(`
        id,
        pickup_city,
        destination_city,
        parcel_category,
        weight_kg,
        offered_price_kes,
        delivery_deadline,
        status,
        created_at,
        bids (
          id,
          bid_price,
          message,
          status,
          created_at,
          traveler_id,
          trip_id,
          user_profiles!bids_traveler_id_fkey (
            full_name,
            username,
            trust_score
          )
        ),
        deliveries (
          id,
          status,
          user_profiles!deliveries_traveler_id_fkey (
            full_name,
            username
          )
        )
      `)
      .eq('sender_id', user.id)
      .order('created_at', { ascending: false });

    if (fetchError) {
      setError(fetchError.message);
      setLoading(false);
      return;
    }

    const mapped: OrderRecord[] = (data || []).map((o: any) => {
      const delivery = Array.isArray(o.deliveries) ? o.deliveries[0] : o.deliveries;
      const bids: BidOnOrder[] = (o.bids || []).map((b: any) => ({
        id: b.id,
        tripId: b.trip_id || null,
        bidPrice: b.bid_price ?? null,
        message: b.message || '',
        status: b.status || 'pending',
        createdAt: b.created_at,
        travelerName: b.user_profiles?.full_name || 'Traveler',
        travelerUsername: b.user_profiles?.username || '',
        travelerRating: b.user_profiles?.trust_score ?? null,
        travelerId: b.traveler_id || '',
      }));

      return {
        id: o.id,
        pickupCity: o.pickup_city || '—',
        destinationCity: o.destination_city || '—',
        parcelCategory: o.parcel_category || '—',
        weightKg: o.weight_kg ?? 0,
        offeredPriceKes: o.offered_price_kes ?? 0,
        deliveryDeadline: o.delivery_deadline || null,
        status: (o.status || 'open') as OrderStatus,
        createdAt: o.created_at,
        bids,
        deliveryStatus: delivery?.status || null,
        deliveryId: delivery?.id || null,
        travelerName: delivery?.user_profiles?.full_name || null,
        travelerUsername: delivery?.user_profiles?.username || null,
      };
    });

    setOrders(mapped);
    setLoading(false);
  }, [user]);

  // ─── Accept bid handler ────────────────────────────────────────────────────
  const handleAcceptBid = useCallback(async (orderId: string, bid: BidOnOrder) => {
    if (!user) return;
    setAcceptingBid(bid.id);
    setAcceptError(null);

    const supabase = createClient();

    try {
      // 1. Accept the selected bid
      const { error: acceptErr } = await supabase
        .from('bids')
        .update({ status: 'accepted' })
        .eq('id', bid.id);
      if (acceptErr) throw acceptErr;

      // 2. Reject all other pending bids for this order
      const { error: rejectErr } = await supabase
        .from('bids')
        .update({ status: 'rejected' })
        .eq('order_id', orderId)
        .eq('status', 'pending')
        .neq('id', bid.id);
      if (rejectErr) throw rejectErr;

      // 3. Update order status to matched
      const { error: orderErr } = await supabase
        .from('orders')
        .update({ status: 'matched', matched_bid_id: bid.id, matched_trip_id: bid.tripId })
        .eq('id', orderId);
      if (orderErr) throw orderErr;

      // 4. Fetch the order details for delivery record
      const { data: orderData } = await supabase
        .from('orders')
        .select('pickup_city, destination_city, parcel_category, weight_kg, offered_price_kes')
        .eq('id', orderId)
        .maybeSingle();

      // 5. Fetch traveler profile
      const { data: travelerProfile } = await supabase
        .from('user_profiles')
        .select('full_name, username, trust_score, phone, is_approved_traveler')
        .eq('id', bid.travelerId)
        .maybeSingle();

      // 6. Fetch trip data if available
      let tripData: any = null;
      if (bid.tripId) {
        const { data: trip } = await supabase
          .from('trips')
          .select('travel_date, departure_time, estimated_arrival, transport_method, guarantor_name, guarantor_phone')
          .eq('id', bid.tripId)
          .maybeSingle();
        tripData = trip;
      }

      // 7. Build departure / arrival timestamps
      const travelDate = tripData?.travel_date || new Date().toISOString().split('T')[0];
      const departureTimeStr = tripData?.departure_time
        ? `${travelDate}T${tripData.departure_time}`
        : null;
      const estimatedArrivalStr = tripData?.estimated_arrival
        ? `${travelDate}T${tripData.estimated_arrival}`
        : null;

      // 8. Generate delivery PIN and QR code
      const deliveryPin = Math.floor(1000 + Math.random() * 9000).toString();
      const qrCode = `QR-${orderId.slice(0, 8).toUpperCase()}-${bid.id.slice(0, 4).toUpperCase()}`;

      const pickupCity = orderData?.pickup_city || '—';
      const destinationCity = orderData?.destination_city || '—';
      const parcelCategory = orderData?.parcel_category || '—';
      const weightKg = orderData?.weight_kg ?? 0;
      const offeredPriceKes = orderData?.offered_price_kes ?? bid.bidPrice ?? 0;

      // 9. Create complete delivery record with status 'matched' so it appears in Active Deliveries
      const { data: deliveryData, error: deliveryErr } = await supabase
        .from('deliveries')
        .insert({
          order_id: orderId,
          traveler_id: bid.travelerId,
          sender_id: user.id,
          route: `${pickupCity} → ${destinationCity}`,
          pickup_city: pickupCity,
          destination_city: destinationCity,
          parcel_category: parcelCategory,
          parcel_description: parcelCategory,
          weight_kg: weightKg,
          traveler_name: travelerProfile?.full_name || bid.travelerName,
          traveler_username: travelerProfile?.username || bid.travelerUsername,
          traveler_rating: travelerProfile?.trust_score || bid.travelerRating || 5.0,
          traveler_phone: travelerProfile?.phone || '',
          traveler_verified: !!travelerProfile?.is_approved_traveler,
          guarantor_name: tripData?.guarantor_name || '',
          guarantor_phone: tripData?.guarantor_phone || '',
          transport_method: tripData?.transport_method || 'Bus',
          departure_time: departureTimeStr,
          estimated_arrival: estimatedArrivalStr,
          offered_price_kes: offeredPriceKes,
          delivery_pin: deliveryPin,
          qr_code: qrCode,
          status: 'matched',
          matched_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (deliveryErr && deliveryErr.code !== '23505') throw deliveryErr;

      // 10. Insert initial tracking event
      if (deliveryData?.id) {
        await supabase.from('tracking_events').insert({
          delivery_id: deliveryData.id,
          event_type: 'matched',
          title: 'Traveler Matched',
          description: `${travelerProfile?.full_name || bid.travelerName} accepted to carry your parcel from ${pickupCity} to ${destinationCity}.`,
          location_name: pickupCity,
          recorded_by: user.id,
        });
      }

      // 11. Notify the accepted traveler
      await supabase.from('notifications').insert({
        user_id: bid.travelerId,
        type: 'bid_accepted',
        title: '🎉 Your Bid Was Accepted!',
        message: `Great news! Your bid for the delivery from ${pickupCity} to ${destinationCity} has been accepted. Check your Active Deliveries to proceed.`,
        is_read: false,
        reference_id: deliveryData?.id || null,
        reference_type: 'delivery',
        data: { order_id: orderId, delivery_id: deliveryData?.id },
      });

      // 12. Notify sender (self) about delivery activation
      await supabase.from('notifications').insert({
        user_id: user.id,
        type: 'delivery_activated',
        title: '📦 Delivery Activated',
        message: `Your delivery from ${pickupCity} to ${destinationCity} is now active with ${travelerProfile?.full_name || bid.travelerName}. Track it in Active Deliveries.`,
        is_read: false,
        reference_id: deliveryData?.id || null,
        reference_type: 'delivery',
        data: { order_id: orderId, delivery_id: deliveryData?.id },
      });

      // 13. Refresh orders
      await fetchOrders();

      // 14. Navigate to active deliveries so both parties can see it
      if (deliveryData?.id) {
        window.location.href = `/active-deliveries?delivery=${deliveryData.id}`;
      } else {
        window.location.href = '/active-deliveries';
      }
    } catch (err: any) {
      setAcceptError(err?.message || 'Failed to accept bid. Please try again.');
    } finally {
      setAcceptingBid(null);
    }
  }, [user, fetchOrders]);

  // ─── Realtime subscriptions ────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    fetchOrders();

    const supabase = createClient();

    const flashLive = () => {
      setLiveUpdate(true);
      if (liveFlashRef.current) clearTimeout(liveFlashRef.current);
      liveFlashRef.current = setTimeout(() => setLiveUpdate(false), 3000);
    };

    const ordersChannel = supabase
      .channel(`my-orders-rt-${user.id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'orders', filter: `sender_id=eq.${user.id}` },
        () => { flashLive(); fetchOrders(); })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'bids' },
        () => { flashLive(); fetchOrders(); })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'bids' },
        () => { flashLive(); fetchOrders(); })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'deliveries' },
        () => { flashLive(); fetchOrders(); })
      .subscribe();

    return () => {
      supabase.removeChannel(ordersChannel);
      if (liveFlashRef.current) clearTimeout(liveFlashRef.current);
    };
  }, [user, fetchOrders]);

  const toggleBids = (orderId: string) => {
    setExpandedBids(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  // ─── Derived ───────────────────────────────────────────────────────────────

  const stats: SummaryStats = {
    total:     orders.length,
    open:      orders.filter(o => o.status === 'open').length,
    matched:   orders.filter(o => o.status === 'matched').length,
    inTransit: orders.filter(o => o.status === 'in_transit').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    completed: orders.filter(o => o.status === 'completed').length,
  };

  const filtered = orders.filter(o => {
    const matchesFilter = filter === 'all' || o.status === filter;
    const q = search.toLowerCase();
    const matchesSearch = !q || [o.pickupCity, o.destinationCity, o.parcelCategory, o.travelerName || ''].some(v => v.toLowerCase().includes(q));
    return matchesFilter && matchesSearch;
  });

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-700 text-foreground tracking-tight">My Orders</h1>
            <p className="text-sm text-muted-foreground mt-1">Track all your posted orders, bids received, and delivery status</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Live indicator */}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors ${liveUpdate ? 'bg-emerald-50 border-emerald-200' : 'bg-muted/30 border-border'}`}>
              <span className={`w-2 h-2 rounded-full ${liveUpdate ? 'bg-emerald-500 animate-ping' : 'bg-emerald-400 animate-pulse'}`} />
              <span className={`text-xs font-600 ${liveUpdate ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                {liveUpdate ? 'Updated!' : 'Live'}
              </span>
            </div>
            <button
              onClick={fetchOrders}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card text-sm font-500 text-foreground hover:bg-muted/50 transition-colors"
            >
              <Icon name="ArrowPathIcon" size={15} />
              Refresh
            </button>
            <Link
              href="/create-order"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-500 hover:bg-primary/90 transition-colors"
            >
              <Icon name="PlusCircleIcon" size={15} />
              New Order
            </Link>
          </div>
        </div>

        {/* Accept error banner */}
        {acceptError && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <Icon name="ExclamationCircleIcon" size={18} className="text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700 flex-1">{acceptError}</p>
            <button onClick={() => setAcceptError(null)} className="text-red-500 hover:text-red-700">
              <Icon name="XMarkIcon" size={16} />
            </button>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
          {[
            { label: 'Total Orders', value: stats.total,     icon: 'ClipboardDocumentListIcon', color: 'bg-primary/10 text-primary' },
            { label: 'Open',         value: stats.open,      icon: 'ClockIcon',                 color: 'bg-amber-100 text-amber-700' },
            { label: 'Matched',      value: stats.matched,   icon: 'LinkIcon',                  color: 'bg-blue-100 text-blue-700' },
            { label: 'In Transit',   value: stats.inTransit, icon: 'TruckIcon',                 color: 'bg-orange-100 text-orange-700' },
            { label: 'Delivered',    value: stats.delivered, icon: 'CheckCircleIcon',            color: 'bg-emerald-100 text-emerald-700' },
            { label: 'Completed',    value: stats.completed, icon: 'CheckBadgeIcon',             color: 'bg-green-100 text-green-700' },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${s.color}`}>
                <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={18} />
              </div>
              <div>
                <p className="text-xl font-700 text-foreground leading-none">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filters + Search */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1 flex-shrink-0 flex-wrap">
            {filterOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setFilter(opt.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-500 transition-colors ${
                  filter === opt.value
                    ? 'bg-card shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt.label}
                {opt.value !== 'all' && (
                  <span className="ml-1.5 text-[10px] font-600 opacity-70">
                    {opt.value === 'open' ? stats.open : opt.value === 'matched' ? stats.matched : opt.value === 'in_transit' ? stats.inTransit : opt.value === 'delivered' ? stats.delivered : opt.value === 'completed' ? stats.completed : 0}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="relative flex-1">
            <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by city, parcel type, or traveler…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        {/* Content */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-40 bg-muted rounded-xl animate-pulse" />
            ))}
          </div>
        )}

        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <Icon name="ExclamationCircleIcon" size={18} className="text-red-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-600 text-red-700">Failed to load orders</p>
              <p className="text-xs text-red-600 mt-0.5">{error}</p>
            </div>
            <button onClick={fetchOrders} className="ml-auto text-xs text-red-600 underline">Retry</button>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-16 bg-card border border-border rounded-xl">
            <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="ClipboardDocumentListIcon" size={28} className="text-muted-foreground" />
            </div>
            <p className="text-base font-600 text-foreground mb-1">
              {orders.length === 0 ? 'No orders posted yet' : 'No orders match your filter'}
            </p>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
              {orders.length === 0
                ? 'Post your first delivery order and start receiving bids from travelers.' :'Try adjusting your search or filter to find what you\'re looking for.'}
            </p>
            {orders.length === 0 && (
              <Link
                href="/create-order"
                className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-500 hover:bg-primary/90 transition-colors"
              >
                <Icon name="PlusCircleIcon" size={15} />
                Post an Order
              </Link>
            )}
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="space-y-4">
            {filtered.map(order => {
              const cfg = orderStatusConfig[order.status] || orderStatusConfig['open'];
              const postedDateTime = formatDateTime(order.createdAt);
              const deadlineDate = order.deliveryDeadline
                ? formatDateOnly(order.deliveryDeadline)
                : null;
              const pendingBids = order.bids.filter(b => b.status === 'pending');
              const acceptedBid = order.bids.find(b => b.status === 'accepted');
              const isExpanded = expandedBids.has(order.id);

              return (
                <div
                  key={order.id}
                  className={`bg-card border rounded-xl overflow-hidden transition-shadow hover:shadow-sm ${cfg.bg}`}
                >
                  {/* Main order info */}
                  <div className="p-5 space-y-4">
                    {/* Top row: route + status */}
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Icon name="MapPinIcon" size={15} className="text-primary flex-shrink-0" />
                        <span className="text-sm font-600 text-foreground">
                          {order.pickupCity} → {order.destinationCity}
                        </span>
                        <StatusBadge variant={cfg.variant} label={cfg.label} size="sm" />
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-base font-700 text-primary">
                          KES {order.offeredPriceKes.toLocaleString()}
                        </p>
                        <p className="text-[11px] text-muted-foreground">offered price</p>
                      </div>
                    </div>

                    {/* Order details row */}
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="ArchiveBoxIcon" size={13} className="flex-shrink-0" />
                        <span>{order.parcelCategory} · {order.weightKg} kg</span>
                      </div>
                      {deadlineDate && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Icon name="CalendarDaysIcon" size={13} className="flex-shrink-0" />
                          <span>Deadline: {deadlineDate}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="HashtagIcon" size={13} className="flex-shrink-0" />
                        <span className="font-mono text-[11px]">Order #{order.id.slice(0, 8)}</span>
                      </div>
                    </div>

                    {/* Delivery status (if in transit or delivered) */}
                    {(order.status === 'in_transit' || order.status === 'delivered' || order.status === 'matched') && order.travelerName && (
                      <div className="flex items-center gap-2.5 p-3 rounded-lg bg-background/60 border border-border/50">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-700 text-emerald-700">
                            {order.travelerName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-600 text-foreground">{order.travelerName}</p>
                          {order.travelerUsername && (
                            <p className="text-[11px] text-muted-foreground">@{order.travelerUsername}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {order.status === 'matched' && order.deliveryId && (
                            <Link
                              href={`/delivery-messaging?deliveryId=${order.deliveryId}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-600 hover:bg-primary/90 transition-colors"
                            >
                              <Icon name="ChatBubbleLeftRightIcon" size={12} />
                              Trip Chat
                            </Link>
                          )}
                          <Link
                            href="/active-deliveries"
                            className="inline-flex items-center gap-1 text-xs font-500 text-primary hover:underline"
                          >
                            Track
                            <Icon name="ArrowRightIcon" size={12} />
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* Accept Bid banner — shown for open/matched orders with pending bids */}
                    {(order.status === 'open' || order.status === 'matched') && pendingBids.length > 0 && (
                      <div className="rounded-lg bg-amber-50 border border-amber-200 overflow-hidden">
                        <div className="flex items-center gap-2.5 p-3">
                          <Icon name="TagIcon" size={15} className="text-amber-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-600 text-amber-800">
                              {pendingBids.length} pending bid{pendingBids.length !== 1 ? 's' : ''} awaiting your decision
                            </p>
                            <p className="text-[11px] text-amber-700">Accept a bid to initiate the delivery process</p>
                          </div>
                          <button
                            onClick={() => toggleBids(order.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-600 hover:bg-emerald-700 transition-colors flex-shrink-0"
                          >
                            <Icon name="CheckIcon" size={12} />
                            {isExpanded ? 'Hide Bids' : 'Review & Accept'}
                          </button>
                        </div>

                        {/* Quick-accept list (shown when expanded) */}
                        {isExpanded && pendingBids.length > 0 && (
                          <div className="border-t border-amber-200 px-3 pb-3 pt-2 space-y-2">
                            <p className="text-[10px] font-600 text-amber-700 uppercase tracking-wide">Select a bid to accept</p>
                            {pendingBids.map(bid => (
                              <div key={bid.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-amber-100">
                                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-700 text-primary">{bid.travelerName.charAt(0).toUpperCase()}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-600 text-foreground">{bid.travelerName}</p>
                                  {bid.travelerRating !== null && (
                                    <div className="flex items-center gap-1">
                                      <Icon name="StarIcon" size={10} className="text-yellow-400" />
                                      <span className="text-[10px] text-muted-foreground">{bid.travelerRating.toFixed(1)}</span>
                                    </div>
                                  )}
                                  {bid.message && (
                                    <p className="text-[10px] text-muted-foreground italic truncate max-w-[180px]">"{bid.message}"</p>
                                  )}
                                  <p className="text-[10px] text-muted-foreground">{formatDateTime(bid.createdAt)}</p>
                                </div>
                                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                  {bid.bidPrice !== null && (
                                    <p className="text-sm font-700 text-emerald-700">KES {bid.bidPrice.toLocaleString()}</p>
                                  )}
                                  <button
                                    onClick={() => handleAcceptBid(order.id, bid)}
                                    disabled={acceptingBid === bid.id}
                                    className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-emerald-600 text-white text-[11px] font-600 hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                  >
                                    {acceptingBid === bid.id ? (
                                      <>
                                        <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                        Accepting…
                                      </>
                                    ) : (
                                      <>
                                        <Icon name="CheckIcon" size={11} />
                                        Accept Bid
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Accepted bid highlight */}
                    {acceptedBid && order.status === 'open' && (
                      <div className="flex items-center gap-2.5 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                        <Icon name="CheckCircleIcon" size={15} className="text-emerald-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-600 text-emerald-800">Bid accepted from {acceptedBid.travelerName}</p>
                          {acceptedBid.bidPrice !== null && (
                            <p className="text-[11px] text-emerald-700">KES {acceptedBid.bidPrice.toLocaleString()}</p>
                          )}
                        </div>
                        <Link
                          href="/accepted-bid-details"
                          className="inline-flex items-center gap-1 text-xs font-500 text-emerald-700 hover:underline flex-shrink-0"
                        >
                          View Details
                          <Icon name="ArrowRightIcon" size={12} />
                        </Link>
                      </div>
                    )}

                    {/* Footer: posted date + bids toggle */}
                    <div className="flex items-center justify-between flex-wrap gap-2 pt-1 border-t border-border/50">
                      <p className="text-[11px] text-muted-foreground">Posted {postedDateTime}</p>
                      <div className="flex items-center gap-3">
                        {order.bids.length > 0 && (
                          <button
                            onClick={() => toggleBids(order.id)}
                            className="inline-flex items-center gap-1.5 text-xs font-500 text-primary hover:underline"
                          >
                            <Icon name="TagIcon" size={12} />
                            {order.bids.length} bid{order.bids.length !== 1 ? 's' : ''}
                            {pendingBids.length > 0 && (
                              <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-600">
                                {pendingBids.length} pending
                              </span>
                            )}
                            <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={12} />
                          </button>
                        )}
                        {order.status === 'open' && order.bids.length === 0 && (
                          <span className="text-[11px] text-muted-foreground italic">No bids yet</span>
                        )}
                        {(order.status === 'delivered' || order.status === 'completed') && (
                          <Link
                            href="/reviews"
                            className="inline-flex items-center gap-1 text-xs font-500 text-amber-600 hover:underline"
                          >
                            <Icon name="StarIcon" size={12} />
                            Leave Review
                          </Link>
                        )}
                        {order.status === 'completed' && (
                          <span className="inline-flex items-center gap-1 text-xs font-600 text-success">
                            <Icon name="CheckBadgeIcon" size={12} />
                            Complete
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bids panel (expandable) — shows all bids including non-pending */}
                  {isExpanded && order.bids.length > 0 && (order.status !== 'open' && order.status !== 'matched' || pendingBids.length === 0) && (
                    <div className="border-t border-border/50 bg-background/40 px-5 py-4 space-y-3">
                      <p className="text-[11px] font-600 text-muted-foreground uppercase tracking-wide">
                        Bids Received ({order.bids.length})
                      </p>
                      {order.bids.map(bid => {
                        const bidCfg = bidStatusConfig[bid.status] || bidStatusConfig['pending'];
                        const bidDateTime = formatDateTime(bid.createdAt);
                        return (
                          <div
                            key={bid.id}
                            className="flex items-start gap-3 p-3 rounded-lg bg-card border border-border"
                          >
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-700 text-primary">
                                {bid.travelerName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-600 text-foreground">{bid.travelerName}</p>
                                {bid.travelerUsername && (
                                  <p className="text-[11px] text-muted-foreground">@{bid.travelerUsername}</p>
                                )}
                                {bid.travelerRating !== null && (
                                  <div className="flex items-center gap-1">
                                    <Icon name="StarIcon" size={11} className="text-yellow-400" />
                                    <span className="text-[11px] text-muted-foreground">{bid.travelerRating.toFixed(1)}</span>
                                  </div>
                                )}
                              </div>
                              {bid.message && (
                                <p className="text-xs text-muted-foreground italic leading-relaxed">"{bid.message}"</p>
                              )}
                              <p className="text-[11px] text-muted-foreground">{bidDateTime}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                              {bid.bidPrice !== null && (
                                <p className="text-sm font-700 text-emerald-700">
                                  KES {bid.bidPrice.toLocaleString()}
                                </p>
                              )}
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-600 ${bidCfg.color}`}>
                                {bidCfg.label}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
